import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { confirmationDialogOption, confirmationDialogOptions } from "@common-views/calendar/calendar-view";
import { handleAddScheduledMessageCloseThunk, handleRecurringMessageEditThunk } from "@common/thunks/UserMessagesThunks";
import React, { useCallback, useEffect } from "react";
import { MessageConfirmationDialog } from "./message-confirmation-dialog";
import { useTranslation } from 'react-i18next';
import { namespace } from '@common/config/constants';
import { toastDBCreateError } from '@common/utils/db-create-error-toast';

const EditRecurringDialogConfirmation: React.FC = () => {
  const { t } = useTranslation(namespace);
  const dispatch = useAppDispatch();
  const showConfirmationDialog = useAppSelector(state => state.AddScheduledMessageButtonReducer.showConfirmationDialog);
  const openedFromContext = useAppSelector(state => state.AddScheduledMessageButtonReducer.openedFromContext);

  // #1136: the dialog closes right away. A replacement that did not store leaves the series
  // untouched (#1133), so tell the user the edit did not apply instead of saying nothing.
  const editAndReportFailure = useCallback((scope: 'all' | 'this' | 'thisAndFollowing'): void => {
    dispatch(handleRecurringMessageEditThunk({ scope })).unwrap().catch((error: unknown) => {
      toastDBCreateError(error, t('scheduleMessage.editNotSaved', 'Your change was not saved.'));
    });
  }, [dispatch, t]);

  // Auto-execute with "all messages" scope when opened from calendar sidebar
  useEffect(() => {
    if (showConfirmationDialog && openedFromContext === 'calendar-sidebar') {
      // Automatically use "all messages" scope and execute without showing dialog
      editAndReportFailure('all');
      dispatch(handleAddScheduledMessageCloseThunk());
    }
  }, [showConfirmationDialog, openedFromContext, dispatch, editAndReportFailure]);

  const onEditRecurringDialogClose = (selection?: confirmationDialogOption) => {
    // If no selection was made, just close the dialog
    if (!selection) {
      dispatch(handleAddScheduledMessageCloseThunk());
      return;
    }

    // Map the selection to a scope value
    const scopeMap = {
      "All messages": "all",
      "This message": "this",
      "This and following messages": "thisAndFollowing"
    } as const;
    
    const scope = (scopeMap[selection] ?? 'all') as "all" | "this" | "thisAndFollowing";
    
    // Dispatch the edit action and close
    editAndReportFailure(scope);
    dispatch(handleAddScheduledMessageCloseThunk());
  };

  // Don't show dialog if opened from calendar sidebar (auto-execution handled above)
  if (!showConfirmationDialog || openedFromContext === 'calendar-sidebar') {
    return null;
  }

  return (
    <MessageConfirmationDialog
      title={t('scheduledMessage.confirmation.editRecurring')}
      onClose={onEditRecurringDialogClose}
      options={confirmationDialogOptions}
    />
  );
};

export default EditRecurringDialogConfirmation; 